import jwt from 'jsonwebtoken'
import { APPLICATION_DOMAIN } from '~/api/config'
import { encryptSHA256 } from '~/utils/encryption'

// PostHog feature flag that turns email-verification enforcement on.
export const ENFORCE_EMAIL_VERIFICATION_FLAG = 'enforce_email_verification'

// Auth0 id_token claim namespace carrying the user profile (hasSso).
export const PROFILE_CLAIM = 'https://importgenius.com/profile'

// Cookie ig-console reads on /account/user to capture the PostHog event
// "Identity verification - Existing user - Enforcement redirect", then deletes.
// Name, plain-string value and options are a contract shared with ig-console.
export const ENFORCEMENT_REDIRECT_COOKIE_NAME = 'ig_email_verification_redirect'

// Lifetime of the enforcement redirect cookie, in seconds (10 minutes).
export const ENFORCEMENT_REDIRECT_COOKIE_MAX_AGE = 600

// Session cookie ig-console sets at the end of signup onboarding. Its value is
// an opaque per-login value: the first 32 characters of the lowercase hex
// SHA-256 of `${clientId}:${iat}` (core client id, Auth0 id_token iat in
// seconds). While it matches the value Lighthouse recomputes, Lighthouse skips the email-verification redirect so
// the user keeps their first experience; cleared on logout. Name, value and
// options are a contract shared with ig-console and iscanv2.
export const FIRST_EXPERIENCE_COOKIE_NAME = 'first_experience'

// Length of the first_experience value: the leading hex characters kept from
// the SHA-256 digest (part of the cross-app contract).
const FIRST_EXPERIENCE_VALUE_LENGTH = 32

// Module-level bookkeeping. Lighthouse is a SPA, so this state lives for the
// whole page load and is shared by the route middleware and the late-flag
// plugin.
let forcedRedirect = false
let emailVerificationRedirect = false
let guardsSettled = false
// Expected first_experience value for the current user + token. status is
// 'pending' | 'ready' | 'failed'; key is the hash input (`${clientId}:${iat}`),
// so a new login or a refreshed id_token starts a fresh computation.
let firstExperienceCheck = null

/**
 * Records that a higher-priority guard (TOS, migration, MFA, ...) has already
 * started a forced redirect, so the email-verification redirect must not
 * fire on top of it.
 */
export function markForcedRedirect() {
  forcedRedirect = true
}

/**
 * @returns {boolean} true once any higher-priority forced redirect started.
 */
export function hasForcedRedirect() {
  return forcedRedirect
}

/**
 * Records that this page load handed the user off to the console for email
 * verification (used to defeat bfcache restores of the gated page).
 */
export function markEmailVerificationRedirect() {
  emailVerificationRedirect = true
}

/**
 * @returns {boolean} true once the email-verification redirect was issued.
 */
export function hasEmailVerificationRedirect() {
  return emailVerificationRedirect
}

/**
 * Records that the router middleware chain reached the email-verification
 * guard, i.e. every earlier guard has had its chance to mark a forced
 * redirect. Until then the late-flag plugin must not redirect, otherwise a
 * PostHog callback could win a race against a guard still running.
 */
export function markGuardsSettled() {
  guardsSettled = true
}

/**
 * Reads hasSso from the id_token profile claim.
 *
 * @param {string} token - The id_token, optionally 'Bearer '-prefixed.
 * @returns {boolean} true only when the claim is strictly boolean true.
 */
function hasSsoClaim(token) {
  if (!token) {
    return false
  }

  const decoded = jwt.decode(token.replace('Bearer ', ''))
  const profile = decoded && decoded[PROFILE_CLAIM]

  return !!profile && profile.hasSso === true
}

/**
 * Whether the address counts as verified. Mirrors ig-console isEmailVerified
 * (src/stores/user.js), checked in the same order:
 *
 * 1. account-info `email_verified` is 1;
 * 2. the Auth0 `hasSso` claim is set (the identity provider asserted the
 *    address, so no verification link was ever sent);
 * 3. `hasActiveTeamThroughInvite` is 1 (the account exists because someone
 *    accepted an invite mailed to this address).
 *
 * @param {object} user - auth-next user (BFF /api/account-info payload).
 * @param {string} token - The id_token, optionally 'Bearer '-prefixed.
 * @returns {boolean}
 */
export function isEmailVerified(user, token) {
  return (
    Number(user.email_verified) === 1 ||
    hasSsoClaim(token) ||
    Number(user.hasActiveTeamThroughInvite) === 1
  )
}

/**
 * Whether PostHog has identified the session as this user. The flag is a
 * percentage rollout bucketed on the distinct_id, and before identify (Navbar)
 * that is a fresh anonymous id, so a result read then is a coin flip the
 * other apps, which share the identified id through the PostHog cookie, would
 * not agree with. Email conditions (the whitelist) also only match an
 * identified person. The gate therefore acts only on flags evaluated for the
 * email.
 *
 * @param {*} distinctId - $posthog.get_distinct_id().
 * @param {string} email - The account-info email.
 * @returns {boolean}
 */
export function isPosthogIdentifiedAs(distinctId, email) {
  if (!email || typeof distinctId !== 'string') {
    return false
  }

  return distinctId.trim().toLowerCase() === email.trim().toLowerCase()
}

/**
 * Whether the user must be sent to verify their email. Only an explicit
 * `email_verified` of 0 qualifies: a missing or null field reads as unknown
 * and never gates.
 *
 * @param {object} user - auth-next user (BFF /api/account-info payload).
 * @param {string} token - The id_token, optionally 'Bearer '-prefixed.
 * @returns {boolean}
 */
export function requiresEmailVerification(user, token) {
  if (!user) {
    return false
  }

  if (user.email_verified === undefined || user.email_verified === null) {
    return false
  }

  return !isEmailVerified(user, token)
}

/**
 * Sets the enforcement redirect cookie ahead of the redirect to the console.
 * The value is the gated page's host + path (no protocol, query or hash).
 * Analytics must never block enforcement, so a missing $cookies or a failing
 * set is skipped rather than thrown.
 *
 * @param {object} $cookies - cookie-universal instance (ctx.app.$cookies).
 * @param {object} location - The page's location (window.location).
 */
export function setEnforcementRedirectCookie($cookies, location) {
  if (!$cookies) {
    return
  }

  try {
    $cookies.set(
      ENFORCEMENT_REDIRECT_COOKIE_NAME,
      `${location.host}${location.pathname}`,
      {
        domain: APPLICATION_DOMAIN,
        path: '/',
        maxAge: ENFORCEMENT_REDIRECT_COOKIE_MAX_AGE,
        sameSite: 'lax'
      }
    )
  } catch (error) {
    // Analytics only: never let it block the redirect.
  }
}

/**
 * First 32 characters of the lowercase hex SHA-256 of UTF-8
 * `${clientId}:${iat}`: the value ig-console writes to the first_experience
 * cookie. Rejects when Web Crypto is missing or the digest fails (delegates to
 * encryptSHA256).
 *
 * @param {string|number} clientId - Core client id (decimal).
 * @param {string|number} iat - id_token `iat` claim, in seconds (decimal).
 * @returns {Promise<string>} 32 lowercase hex characters.
 */
export function computeFirstExperienceValue(clientId, iat) {
  return encryptSHA256(`${clientId}:${iat}`).then(hash =>
    hash.slice(0, FIRST_EXPERIENCE_VALUE_LENGTH)
  )
}

/**
 * Reads the first_experience cookie as a RAW string. cookie-universal parses
 * JSON by default, which could turn an all-digit hex hash into a number, so
 * parsing is switched off.
 *
 * @param {object} $cookies - cookie-universal instance (ctx.app.$cookies).
 * @returns {string|null} raw value; null when absent, empty or unreadable.
 */
export function getFirstExperienceCookie($cookies) {
  if (!$cookies) {
    return null
  }

  try {
    const value = $cookies.get(FIRST_EXPERIENCE_COOKIE_NAME, {
      parseJSON: false
    })

    return typeof value === 'string' && value !== '' ? value : null
  } catch (error) {
    return null
  }
}

/**
 * Reads the `iat` claim (seconds) of an id_token as a decimal string.
 *
 * @param {string} token - The id_token, optionally 'Bearer '-prefixed.
 * @returns {string|null} null when the token or claim is missing or invalid.
 */
function getTokenIat(token) {
  if (!token) {
    return null
  }

  const decoded = jwt.decode(token.replace('Bearer ', ''))
  const iat = decoded && decoded.iat

  return typeof iat === 'number' && isFinite(iat) ? String(iat) : null
}

/**
 * Starts (once per user + token) the computation of the expected
 * first_experience value and returns its bookkeeping record. Never throws.
 * Missing id or iat is a settled 'failed' record, not a pending one.
 *
 * @param {object} user - auth-next user (BFF /api/account-info payload); `id`
 * is the core client id, the same field iscanv2 reads from account-info.
 * @param {string} token - The id_token, optionally 'Bearer '-prefixed.
 * @returns {{status: string, value: (string|undefined), promise: Promise}}
 */
function startFirstExperienceCheck(user, token) {
  let clientId = null
  let iat = null

  try {
    if (user && user.id !== undefined && user.id !== null) {
      clientId = `${user.id}`
    }

    iat = getTokenIat(token)
  } catch (error) {
    // Treated as missing below.
  }

  if (!clientId || !iat) {
    return { key: null, status: 'failed' }
  }

  const key = `${clientId}:${iat}`

  if (firstExperienceCheck && firstExperienceCheck.key === key) {
    return firstExperienceCheck
  }

  const check = { key, status: 'pending' }

  check.promise = computeFirstExperienceValue(clientId, iat)
    .then(value => {
      check.value = value
      check.status = 'ready'
    })
    .catch(() => {
      check.status = 'failed'
    })
  firstExperienceCheck = check

  return check
}

/**
 * Starts computing the expected first_experience value early (route
 * middleware, before the PostHog flag arrives) so the guard can usually answer
 * synchronously later. No-op without the cookie. Never throws.
 *
 * @param {object} ctx
 * @param {object} ctx.$auth - auth-next instance.
 * @param {object} [ctx.$cookies] - cookie-universal instance.
 */
export function primeFirstExperienceValue({ $auth, $cookies }) {
  try {
    if (
      !$auth ||
      !$auth.loggedIn ||
      !$auth.user ||
      getFirstExperienceCookie($cookies) === null
    ) {
      return
    }

    startFirstExperienceCheck(
      $auth.user,
      $auth.strategy && $auth.strategy.token.get()
    )
  } catch (error) {
    // Priming is an optimisation only.
  }
}

/**
 * Removes the first-experience cookie. Domain and path must match how
 * ig-console set it. cookie-universal's remove mutates the options it is
 * given, so a fresh object is passed every time. A missing $cookies or a
 * failing remove is skipped rather than thrown so logout never breaks.
 *
 * @param {object} $cookies - cookie-universal instance (ctx.app.$cookies).
 */
export function removeFirstExperienceCookie($cookies) {
  if (!$cookies) {
    return
  }

  try {
    $cookies.remove(FIRST_EXPERIENCE_COOKIE_NAME, {
      domain: '.importgenius.com',
      path: '/',
      sameSite: 'lax'
    })
  } catch (error) {
    // Never let cookie cleanup block logout.
  }
}

/**
 * PLATFORM-184: redirects an unverified user to the console User Information
 * page when the enforce_email_verification flag is on. Secondary to every
 * other forced redirect (TOS, migration, MFA, checkPfsFlag), which always
 * take priority.
 *
 * @param {object} ctx
 * @param {object} ctx.$auth - auth-next instance.
 * @param {object} ctx.$posthog - posthog-js instance.
 * @param {object} [ctx.$cookies] - cookie-universal instance; when missing the
 * enforcement-redirect cookie is skipped but the redirect still happens.
 * @returns {boolean} true when a redirect was issued.
 */
export function enforceEmailVerification({ $auth, $posthog, $cookies }) {
  if (!guardsSettled || emailVerificationRedirect || forcedRedirect) {
    return false
  }

  if (!$auth || !$auth.loggedIn || !$auth.user) {
    return false
  }

  const user = $auth.user

  // checkPfsFlag (Navbar) redirects these users and has priority.
  if (user.requireInfoUpdate === 1 || user.require_password_change === 1) {
    return false
  }

  // TOS has priority. termsIsRequired reads contract off the accounts store,
  // which Navbar only fills after the middleware chain on a first load, so it
  // can miss; $auth.user carries the same account-info contract field.
  if (Number(user.contract) === 0) {
    return false
  }

  if (process.env.POSTHOG_DISABLE_FEATURE_FLAGS === 'true') {
    return false
  }

  if (
    !$posthog ||
    $posthog.isFeatureEnabled(ENFORCE_EMAIL_VERIFICATION_FLAG) !== true
  ) {
    return false
  }

  // Flags read before identify are for an anonymous id; identify (Navbar)
  // reloads them and the onFeatureFlags plugin brings us back here.
  if (!isPosthogIdentifiedAs($posthog.get_distinct_id(), user.email)) {
    return false
  }

  const token = $auth.strategy && $auth.strategy.token.get()

  if (!requiresEmailVerification(user, token)) {
    return false
  }

  const consoleAppUrl = process.env.CONSOLE_APP_URL

  if (!consoleAppUrl) {
    return false
  }

  // First experience right after onboarding: do not redirect (nor mark) while
  // the cookie holds this login's hash. The hash is async (Web Crypto), so
  // while it computes we hold the redirect and re-run once it settles; a
  // failed computation counts as no marker, so the re-run redirects.
  const firstExperienceValue = getFirstExperienceCookie($cookies)

  if (firstExperienceValue !== null) {
    const check = startFirstExperienceCheck(user, token)

    if (check.status === 'pending') {
      check.promise.then(() =>
        enforceEmailVerification({ $auth, $posthog, $cookies })
      )

      return false
    }

    if (check.status === 'ready' && check.value === firstExperienceValue) {
      return false
    }
  }

  markForcedRedirect()
  markEmailVerificationRedirect()
  setEnforcementRedirectCookie($cookies, window.location)
  window.location.replace(`${consoleAppUrl}/account/user`)

  return true
}
